"use client";

import React, { useState, ChangeEvent, useEffect } from "react";

interface PercentageSliderProps {
  totalValue?: number;
  initialPercentage?: number;
  onChange?: (value: { actualValue: string; percentage: number }) => void;
}

const PercentageSlider: React.FC<PercentageSliderProps> = ({ totalValue = 1000, initialPercentage = 0, onChange }) => {
  const [actualValue, setActualValue] = useState<string>("");
  const [percentage, setPercentage] = useState<number>(initialPercentage);

  // Initialize with initial percentage
  useEffect(() => {
    if (Number.isInteger(initialPercentage)) {
      const initial = calculateActualValue(initialPercentage);
      setActualValue(Math.round(initial).toString());
      setPercentage(initialPercentage);
      onChange?.({
        actualValue: Math.round(initial).toString(),
        percentage: initialPercentage,
      });
    }
  }, [initialPercentage, totalValue]);

  const calculatePercentage = (value: number): number => {
    return (value / totalValue) * 100;
  };

  const calculateActualValue = (percent: number): number => {
    return (percent * totalValue) / 100;
  };

  const formatNumber = (value: string | number): string => {
    if (!value) return "";
    const numberValue = typeof value === "string" ? parseFloat(value) : value;
    return `AED ${numberValue.toLocaleString("en-US")}`;
  };

  const parseFormattedNumber = (value: string): string => {
    return value.replace(/[^0-9.-]+/g, "");
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const rawValue = parseFormattedNumber(e.target.value);

    if (rawValue === "") {
      setActualValue("");
      setPercentage(0);
      onChange?.({ actualValue: "", percentage: 0 });
      return;
    }

    const clampedValue = Math.min(totalValue, Math.max(0, Number(rawValue)));
    const newActualValue = clampedValue.toString();
    const newPercentage = calculatePercentage(clampedValue);

    setActualValue(newActualValue);
    setPercentage(newPercentage);
    onChange?.({ actualValue: newActualValue, percentage: newPercentage });
  };

  const handleSliderChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newPercentage = Math.round(Number(e.target.value) / 5) * 5;
    const newActualValue = Math.round(calculateActualValue(newPercentage)).toString();

    setPercentage(newPercentage);
    setActualValue(newActualValue);
    onChange?.({ actualValue: newActualValue, percentage: newPercentage });
  };

  const handleInputBlur = (): void => {
    if (actualValue === "") {
      setPercentage(0);
      onChange?.({ actualValue: "", percentage: 0 });
    }
  };

  const displayValue = actualValue ? formatNumber(actualValue) : "";

  return (
    <div className="relative mt-8 w-full">
      <div className="relative">
        <input
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          className="w-full rounded-xl border border-black/10 p-5"
          placeholder=""
        />
        <div className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-500">
          {actualValue && `(${percentage.toFixed(1)}%)`}
        </div>
      </div>
      <input
        type="range"
        value={percentage}
        onChange={handleSliderChange}
        min={0}
        max={100}
        step={5}
        className="custom-range absolute bottom-0 left-1/2 h-1 w-11/12 -translate-x-1/2 cursor-pointer rounded-full !bg-black/10 accent-primary"
      />
    </div>
  );
};

export default PercentageSlider;
